import { createContext, useCallback, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { Issuer } from 'src/utils/auth';
import { login, isTokenValid, logout as clearPersistedCredentials } from 'src/services/authServices';
import httpServices from 'src/services/httpServices';
import { authApi } from 'src/api/auth';

const STORAGE_KEY = 'token';

var ActionType;
(function (ActionType) {
  ActionType['INITIALIZE'] = 'INITIALIZE';
  ActionType['SIGN_IN'] = 'SIGN_IN';
  ActionType['SIGN_UP'] = 'SIGN_UP';
  ActionType['SIGN_OUT'] = 'SIGN_OUT';
})(ActionType || (ActionType = {}));

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },

  SIGN_IN: (state, action) => {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user
    };
  },

  SIGN_UP: (state, action) => {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user
    };
  },

  SIGN_OUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  })
};

const reducer = (state, action) =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

export const AuthContext = createContext({
  ...initialState,
  issuer: Issuer.JWT,
  signIn: () => Promise.resolve(),
  signUp: () => Promise.resolve(),
  signOut: () => Promise.resolve()
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      // Check if token exists and is valid (not expired)
      if (isTokenValid()) {
        const token = localStorage.getItem(STORAGE_KEY);
        httpServices.setAccessToken(token);

        // Optional: Call /me API
        const user = { isLoggedIn: true };

        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: true,
            user
          }
        });
      } else {
        clearPersistedCredentials();

        dispatch({
          type: ActionType.INITIALIZE,
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    } catch (error) {
      dispatch({
        type: ActionType.INITIALIZE,
        payload: {
          isAuthenticated: false,
          user: null
        }
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  const signIn = useCallback(async (email, password) => {
    
    await login(email, password);

    const user = { email, isLoggedIn: true };

    dispatch({
      type: ActionType.SIGN_IN,
      payload: { user }
    });
  }, []);

  const signUp = useCallback(async (email, name, password) => {
    const { accessToken } = await authApi.signUp({ email, name, password });

    localStorage.setItem(STORAGE_KEY, accessToken);

    const user = { email, name, isLoggedIn: true };

    dispatch({
      type: ActionType.SIGN_UP,
      payload: { user }
    });
  }, []);

  const signOut = useCallback(() => {
    clearPersistedCredentials();
    dispatch({ type: ActionType.SIGN_OUT });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        issuer: Issuer.JWT,
        signIn,
        signUp,
        signOut
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const AuthConsumer = AuthContext.Consumer;
